/**
 * Starting from a template. §6.4.
 *
 * Offered when a design is still blank, because that is the only moment when
 * taking one costs nothing: a template replaces the whole document, and doing
 * that to work a customer has already done would be a way to lose an
 * afternoon. Once anything has been drawn the gallery is still reachable from
 * the Designs panel, and it asks first.
 *
 * The templates are the shop's own, listed on this product. Nothing here
 * decides what fits - the server does, having the sizes - and a template drawn
 * for a different shape is shown and marked rather than hidden, because a shop
 * that has listed it presumably meant to.
 */

import { useEffect, useState } from 'react';
import type { Template } from '../api';
import type { TrimboxDocument } from '../model/types';
import { formatSize } from '../model/units';
import { useEditor } from '../state/store';
import { takeTemplate } from '../templates';

interface Props {
  /**
   * The templates to offer, already fetched.
   *
   * Handed in rather than looked up here, and for a reason worth recording:
   * fetching inside meant the sheet existed - and covered the canvas - while it
   * was still finding out whether it had anything to show. On a product with no
   * templates that was a modal flashing up over the editor and swallowing the
   * customer's first click.
   */
  found: Template[];
  /** Called with the chosen template's document. */
  onPick: ( document: TrimboxDocument ) => void;
  onClose: () => void;
  /** Shown when the customer has drawn nothing yet. */
  greeting?: boolean;
}

export function Templates( { found, onPick, onClose, greeting }: Props ) {
  const [ taking, setTaking ] = useState( '' );
  const [ problem, setProblem ] = useState( '' );

  useEffect( () => {
    const key = ( event: KeyboardEvent ) => {
      if ( event.key === 'Escape' ) onClose();
    };

    window.addEventListener( 'keydown', key );

    return () => window.removeEventListener( 'keydown', key );
  }, [ onClose ] );

  const take = async ( template: Template ) => {
    setTaking( template.uuid );
    setProblem( '' );

    try {
      const current = useEditor.getState().doc;

      if ( current ) await takeTemplate( template.uuid, onPick, current );
    } catch {
      setProblem( 'That template could not be opened. Please try another.' );
      setTaking( '' );
    }
  };

  return (
    <div className="tb-templates" role="dialog" aria-modal="true" aria-label="Templates">
      <div className="tb-templates__sheet">
        <header className="tb-templates__head">
          <div>
            <h2 className="tb-templates__title">
              { greeting ? 'Start from a template' : 'Templates' }
            </h2>
            <p className="tb-templates__sub">
              { greeting
                ? 'Pick one to begin, or start with a blank card.'
                : 'Picking one replaces everything on the card.' }
            </p>
          </div>

          <button type="button" className="tb-btn tb-btn--quiet" onClick={ onClose }>
            { greeting ? 'Start blank' : 'Close' }
          </button>
        </header>

        { problem && <p className="tb-templates__problem">{ problem }</p> }

        { found.length === 0 && (
          <p className="tb-templates__waiting">This product has no templates yet.</p>
        ) }

        { found.length > 0 && (
          <ul className="tb-templates__grid">
            { found.map( ( template ) => (
              <li key={ template.uuid }>
                <button
                  type="button"
                  className="tb-templates__card"
                  disabled={ taking !== '' }
                  onClick={ () => void take( template ) }
                >
                  <span className="tb-templates__shot">
                    { template.thumb ? (
                      <img src={ template.thumb } alt="" loading="lazy" />
                    ) : (
                      <span className="tb-templates__blank" />
                    ) }
                  </span>

                  <span className="tb-templates__name">{ template.title }</span>

                  <span className="tb-templates__note">
                    { template.pages > 1 ? `${ template.pages } sides` : '1 side' }

                    {/*
                      Said plainly rather than hidden. A template drawn for a
                      different card still works - it is placed as it was drawn
                      and can be moved - but a customer who is not told will
                      think the tool has misplaced everything.
                    */}
                    { ! template.fits && (
                      <span className="tb-templates__misfit">
                        {' '}· drawn at { formatSize( template.size.w, template.size.h, useEditor.getState().unit ) }
                      </span>
                    ) }
                  </span>

                  { taking === template.uuid && <span className="tb-templates__taking">Opening…</span> }
                </button>
              </li>
            ) ) }
          </ul>
        ) }
      </div>
    </div>
  );
}

/** 90 rather than 90.00, which is how a printer says it. */
